import { useEffect, useMemo, useState } from 'react';

import { externalClient as externalClientService } from '@services/externalClient';

import { EXTERNAL_CLIENTS_MAX_PER_PAGE, FIRST_PAGE } from '../constants';

/**
 * External clients of a store perimeter, as both the orders list (options of its client filter) and
 * the order form (its `Client` dropdown) need them: a client is only orderable from a store it is
 * mapped to.
 *
 * The perimeter is compared by content, not by reference — the callers derive it from a store
 * selection that is rebuilt on every render — and a query whose perimeter changed while it was in
 * flight is dropped rather than applied late.
 *
 * @param {Object} params
 * @param {Array<string>} params.storeIds - The stores whose clients are wanted
 * @param {Function} params.onError - Called when the fetch fails, to surface the caller's own message
 *
 * @returns {Array<Object>} The external clients, empty while loading or on an empty perimeter
 */
export const useExternalClientsOfStores = ({ storeIds, onError }) => {
  const [externalClients, setExternalClients] = useState([]);

  const storeIdsKey = useMemo(() => JSON.stringify(storeIds), [storeIds]);

  useEffect(() => {
    let isCurrentQuery = true;

    const fetchExternalClients = async () => {
      if (!storeIds.length) {
        setExternalClients([]);

        return;
      }

      try {
        const { externalClients: clients } =
          await externalClientService.getPaginatedExternalClients(
            {
              page: FIRST_PAGE,
              limit: EXTERNAL_CLIENTS_MAX_PER_PAGE,
              search: '',
              orderBy: 'name',
              orderType: 'asc',
            },
            { storeIds },
          );

        if (!isCurrentQuery) {
          return;
        }

        setExternalClients(clients ?? []);
      } catch {
        if (!isCurrentQuery) {
          return;
        }

        setExternalClients([]);

        onError();
      }
    };

    fetchExternalClients();

    return () => {
      isCurrentQuery = false;
    };
  }, [storeIdsKey]);

  return externalClients;
};
